﻿module Client.ContractPage.PurchaseOrders.OrderItemReport

open FSharpPlus
open Client
open Elmish
open Fable.FontAwesome
open Feliz.Bulma
open Feliz.UseElmish
open PortalLibs.Client.Util
open ContractPageModel
open Model
open PortalLibs.Shared
open Feliz
open Shared

let middle = prop.style[style.textAlign.center]
let left = prop.style[style.textAlign.left]
let right = prop.style[style.textAlign.right]

let twoDigitsCurrency =
  float
  >> formatNumber false MinusSign false AllTwoDigits

type State = { Expanded: PurchaseOrderId Set }
type Msg = | Toggle of PurchaseOrderId
let init () = { Expanded = Set.empty }, Cmd.none
let update msg state =
  match msg with
  | Toggle id ->
    let updated =
      match Set.contains id state.Expanded with
      | true -> Set.remove id state.Expanded
      | false -> Set.add id state.Expanded
    { state with Expanded = updated }, Cmd.none

let TdCosts style children costs = [
  Html.td [ style; prop.children [ children <| twoDigitsCurrency costs.Current ] ]
  Html.td [ style; prop.children [ children <| twoDigitsCurrency costs.Received ] ]
  Html.td [ style; prop.children [ children <| twoDigitsCurrency costs.Invoiced ] ]
  Html.td [ style; prop.children [ children <| twoDigitsCurrency costs.Remaining ] ]
]

let Row =
  React.memo<{| Po: PurchaseOrder; Toggle: PurchaseOrderId -> unit; Expanded: bool |}>(
    name = "OrderItemReportRow",
    withKey = (fun props -> (props.Po.ContractId, props.Po.Number).ToString()),
    areEqual = Fable.React.Helpers.equalsButFunctions,
    render = fun props ->
      let po = props.Po
      let toggle = props.Toggle
      let expanded = props.Expanded
      React.fragment [
        Html.tr [
          if expanded then prop.style [ Config.quantityColor ]
          prop.onClick(fun _ -> { ContractId = po.ContractId; Number = po.Number } |> toggle)
          prop.children [
            Html.td [ left; prop.children [ Html.a [Html.strong po.Number] ] ]
            Html.td [ left; prop.children [ Html.a [Html.strong po.Description] ] ]
            Html.td [ left; prop.children [ Html.a [
              Html.div [
                prop.style [ style.display.flex; style.justifyContent.spaceBetween ]
                prop.children [
                  Html.strong po.VendorName
                  Bulma.icon [ prop.style[ style.color "#b5b5b5" ]
                               prop.children[ Fa.i [
                                 if expanded then Fa.Solid.ChevronUp else Fa.Solid.ChevronDown
                               ] []]]
                ]
              ]
            ] ] ]
            if expanded
            then
              Html.td [prop.colSpan 10]
            else
              Html.td [prop.colSpan 6]
              yield! TdCosts right Html.strong po.TotalCosts
          ]
        ]
        if expanded then
          yield! po.Items |> List.map(fun poi ->
            Html.tr [
              Html.td [ right; prop.text poi.ItemNumber ]
              Html.td [ left; prop.text poi.Description ]
              Html.td []
              Html.td [ left; prop.text poi.UnitOfMeasure ]
              Html.td [ right; prop.text (twoDigitsCurrency poi.UnitCost) ]
              yield! TdCosts right Html.text poi.Quantity
              yield! TdCosts right Html.text poi.Cost
            ])
          Html.tr [
            Html.td []
            Html.td [ left; prop.children [ Html.strong "TOTALS"] ]
            Html.td [ prop.colSpan 7 ]
            yield! TdCosts right Html.text po.TotalCosts
          ]
      ])

let View =
  React.memo<{| Po: PurchaseOrder list option
                Model: Model
                Dispatch: ContractPageModel.Msg -> unit
                AddRequesting: bool
                Contract: Contract |}> (
    name = "OrderItemReport",
    withKey = (fun props -> props.Contract.Id.ToString()),
    render = fun props ->
      
      let state, toggle = React.useElmish(init, update, [| |])
      
      props.Po
      |> loadingMessageOrContent "Loading Purchase Orders..." (fun po ->
        let addRequesting = props.AddRequesting
        let contract = props.Contract
        let dispatch = props.Dispatch
        
        Html.div [
          if addRequesting then
            yield Html.div [
              prop.style [ style.display.flex; style.justifyContent.flexEnd; style.alignItems.center ]
              prop.children [
                Bulma.button.button [
                  prop.style [ style.marginBottom(length.rem 1.5) ]
                  prop.onClick (fun _ -> contract.Id |> Some |> RequestPurchaseOrderContractId |> dispatch)
                  prop.text "Request Purchase Order" 
                ]
              ]
            ]
          yield
            Bulma.table [
              table.isFullWidth
              table.isHoverable
              prop.style [Styles.stickyTableBorder]
              prop.children [
                Html.thead [
                  prop.style (Styles.sticky())
                  prop.children [
                    Html.tr [
                      prop.children [
                        Html.th [ prop.colSpan 5; prop.style[style.backgroundColor.white] ]
                        Html.th [
                          prop.colSpan 4
                          prop.style [ style.textAlign.center; Config.quantityColor ]
                          prop.text "Quantity (units)"
                        ]
                        Html.th [
                          prop.colSpan 4
                          prop.style [ style.textAlign.center; Config.costColor ]
                          prop.text "Cost"
                        ]
                      ]
                    ]
                    Html.tr [
                      Html.th [
                        prop.style [ style.textAlign.left; style.width(length.px 50) ]
                        prop.text "PO ITEM"
                      ]
                      Html.th [left; prop.text "Description"]
                      Html.th [left; prop.text "Vendor"]
                      Html.th [left; prop.text "UM"]
                      Html.th [right; prop.text "Unit Cost"]
                      Html.th [right; prop.text "Current"]
                      Html.th [right; prop.text "Received"]
                      Html.th [right; prop.text "Invoiced"]
                      Html.th [right; prop.text "Remaining"]
                      Html.th [right; prop.text "Current"]
                      Html.th [right; prop.text "Received"]
                      Html.th [right; prop.text "Invoiced"]
                      Html.th [right; prop.text "Remaining"]
                    ]
                  ]
                ]
                Html.tbody [
                  yield!
                    match po with
                    | [] -> []
                    | v ->
                      v
                      |>> fun po ->
                        state.Expanded
                        |> Set.contains { ContractId = po.ContractId; Number = po.Number }
                        |> fun found -> Row {| Po = po; Toggle = (Toggle >> toggle); Expanded = found |}
                ]
              ]
            ]
        ])
  )
